import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

// Data passed by the outputs-page section header when the "Review Files ›"
// button is clicked. Client-side only — the section's assets are already in
// memory (loaded by output-assets-{range,ev}-view), so this dialog doesn't hit
// the API. Companion to ReviewOutputFilenamesDialogComponent which asks the
// server-computed "generated vs stored filename" question.
export interface OutputAssetsReviewFilesDialogData {
  sectionName: string;
  assets: readonly {
    id?: string;
    rawUri?: string | null;
    thumbnailUri?: string | null;
    reviewUri?: string | null;
    internalReviewState?: string | null;
    released?: boolean;
  }[];
}

interface Row {
  filename: string;
  hasRawFile: boolean;
  reviewState: string;
  released: boolean;
}

@Component({
  selector: 'app-output-assets-review-files-dialog',
  templateUrl: './output-assets-review-files-dialog.component.html',
  styleUrls: ['./output-assets-review-files-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputAssetsReviewFilesDialogComponent {
  readonly rows: readonly Row[];
  readonly displayedColumns: readonly string[] = ['filename', 'hasFile', 'reviewState', 'released'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: OutputAssetsReviewFilesDialogData,
    public readonly dialogRef: MatDialogRef<OutputAssetsReviewFilesDialogComponent>,
  ) {
    this.rows = (data.assets ?? []).map((asset) => this.toRow(asset));
  }

  get missingFileCount(): number {
    return this.rows.filter((row) => !row.hasRawFile).length;
  }

  get totalCount(): number {
    return this.rows.length;
  }

  private toRow(asset: OutputAssetsReviewFilesDialogData['assets'][number]): Row {
    return {
      filename: this.extractFilename(asset.rawUri ?? asset.reviewUri ?? asset.thumbnailUri ?? ''),
      hasRawFile: this.isPopulatedUri(asset.rawUri),
      reviewState: asset.internalReviewState ?? '—',
      released: asset.released === true,
    };
  }

  private isPopulatedUri(uri: string | null | undefined): boolean {
    return typeof uri === 'string' && uri.trim().length > 0;
  }

  private extractFilename(uri: string): string {
    if (uri.length === 0) return '—';
    const withoutQuery = uri.split('?', 2)[0];
    const lastSlash = withoutQuery.lastIndexOf('/');
    return lastSlash >= 0 ? withoutQuery.slice(lastSlash + 1) : withoutQuery;
  }
}
